import React, { useState } from 'react';
import {
  useGetInboxListQuery,
  useDeleteInboxMutation,
  useTriggerInboxSyncMutation,
} from '@/store/emailInboxApi';
import { EmailInboxConfig } from '@/types/EmailInbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { ConfirmPopover } from '@/components/ui/ConfirmPopover';
import { InboxFormModal } from './InboxFormModal';
import { Plus, Pencil, Trash2, Mail, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

interface Props {
  onViewEmails: (inboxId: number) => void;
  onEdit: (inbox: EmailInboxConfig) => void;
}

export function InboxListTab({ onViewEmails, onEdit }: Props) {
  const { data: inboxes = [], isLoading, refetch } = useGetInboxListQuery();
  const [deleteInbox] = useDeleteInboxMutation();
  const [triggerSync, { isLoading: isSyncing }] = useTriggerInboxSyncMutation();

  const [formOpen, setFormOpen] = useState(false);
  const [newProtocol, setNewProtocol] = useState<'imap' | 'pop3' | 'google_oauth'>('imap');

  function openCreate(protocol: 'imap' | 'pop3' | 'google_oauth' = 'imap') {
    setNewProtocol(protocol);
    setFormOpen(true);
  }

  async function handleDelete(id: number) {
    try {
      await deleteInbox(id).unwrap();
      toast.success('התיבה נמחקה');
    } catch {
      toast.error('שגיאה במחיקת תיבה');
    }
  }

  async function handleSync(id: number) {
    try {
      await triggerSync(id).unwrap();
      toast.success('שלח-קבל הופעל ברקע');
    } catch (err: unknown) {
      const msg = (err as { data?: { detail?: string } })?.data?.detail ?? 'שגיאה בהפעלת שלח-קבל';
      toast.error(msg);
    }
  }

  if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-gray-500">{inboxes.length} תיבות מוגדרות</p>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 ml-1" />
            רענן
          </Button>
          <Button size="sm" variant="outline" onClick={() => openCreate('google_oauth')}>
            <Mail className="h-4 w-4 ml-1" />
            הוסף Gmail
          </Button>
          <Button size="sm" onClick={() => openCreate('imap')}>
            <Plus className="h-4 w-4 ml-1" />
            הוסף IMAP / POP3
          </Button>
        </div>
      </div>

      {inboxes.length === 0 ? (
        <div className="text-center py-16 text-gray-400 border border-dashed rounded-lg">
          <Mail className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p>אין תיבות מנוטרות. לחץ "הוסף תיבה" להתחיל.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {inboxes.map((inbox) => (
            <InboxCard
              key={inbox.id}
              inbox={inbox}
              onEdit={() => onEdit(inbox)}
              onDelete={() => handleDelete(inbox.id)}
              onSync={() => handleSync(inbox.id)}
              onViewEmails={() => onViewEmails(inbox.id)}
              isSyncing={isSyncing}
            />
          ))}
        </div>
      )}

      {/* Modal for new IMAP/POP3 or Gmail (Gmail will redirect for OAuth) */}
      <InboxFormModal
        open={formOpen}
        onOpenChange={setFormOpen}
        initialData={null}
        initialProtocol={newProtocol}
      />
    </div>
  );
}

interface CardProps {
  inbox: EmailInboxConfig;
  onEdit: () => void;
  onDelete: () => void;
  onSync: () => void;
  isSyncing: boolean;
  onViewEmails: () => void;
}

function InboxCard({ inbox, onEdit, onDelete, onSync, isSyncing, onViewEmails }: CardProps) {
  const protocolBadge: Record<string, string> = {
    imap: 'bg-blue-100 text-blue-700',
    pop3: 'bg-purple-100 text-purple-700',
    google_oauth: 'bg-green-100 text-green-700',
  };

  return (
    <div className="border rounded-lg p-4 bg-white shadow-sm flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-900">{inbox.email}</span>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${protocolBadge[inbox.protocol] ?? 'bg-gray-100 text-gray-600'}`}>
            {inbox.protocol_display}
          </span>
          <Badge variant={inbox.is_active ? 'default' : 'secondary'}>
            {inbox.is_active ? 'פעיל' : 'מושבת'}
          </Badge>
          <Badge variant="outline">{inbox.label_display}</Badge>
          <Badge variant="outline">{inbox.direction_display}</Badge>
        </div>
        <div className="mt-1 text-xs text-gray-500 space-y-0.5">
          {inbox.department_name && <p>מחלקה: {inbox.department_name}</p>}
          {inbox.last_checked && (
            <p>בדיקה אחרונה: {new Date(inbox.last_checked).toLocaleString('he-IL')}</p>
          )}
          {inbox.protocol === 'imap' && inbox.imap_folders.length > 0 && (
            <p>תיקיות: {inbox.imap_folders.join(', ')}</p>
          )}
          {/* Gmail connection status */}
          {inbox.protocol === 'google_oauth' && (
            <div className="flex items-center gap-1">
              {inbox.is_gmail_connected ? (
                <>
                  <CheckCircle className="h-3 w-3 text-green-500" />
                  <span className="text-green-700">Gmail מחובר</span>
                  {inbox.gmail_labels.length > 0 && (
                    <span className="text-gray-400">
                      · {inbox.gmail_labels.length} Labels מנוטרות
                    </span>
                  )}
                </>
              ) : (
                <>
                  <AlertTriangle className="h-3 w-3 text-amber-500" />
                  <span className="text-amber-700">Gmail לא מחובר</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
        <Button
          size="sm"
          variant="outline"
          onClick={onSync}
          disabled={isSyncing || inbox.direction === 'send_only'}
          title={inbox.direction === 'send_only' ? 'תיבת שליחה בלבד' : 'הפעלת שלח-קבל'}
        >
          <RefreshCw className="h-4 w-4 ml-1" />
          שלח-קבל
        </Button>
        <Button size="sm" variant="outline" onClick={onViewEmails}>
          <Mail className="h-4 w-4 ml-1" />
          מיילים
        </Button>

        <Button size="sm" variant="ghost" onClick={onEdit}>
          <Pencil className="h-4 w-4" />
        </Button>

        <ConfirmPopover
          message={`למחוק את התיבה ${inbox.email}?`}
          onConfirm={onDelete}
        >
          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700">
            <Trash2 className="h-4 w-4" />
          </Button>
        </ConfirmPopover>
      </div>
    </div>
  );
}

